import React, { useState, useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/router";
import {
    BannersContainer,
    BannerImage,
    IndicatorsContainer,
    IndicatorDot,
    ActiveIndicator,
} from "../../../styles/promoWidget";
import { DLS_TEXT_VARIANTS } from "../../../constants/dls";
import { IBanner } from "../../../interfaces/home";
import { isValidString } from "../../../utils/string";
import { isValidData } from "utils/object";
import { logBannerWidgetClick } from "../analytics";

interface PromoWidgetSuccessViewProps {
    banners: IBanner[];
}

const PromoWidgetSuccessView = ({ banners }: PromoWidgetSuccessViewProps) => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [isAutoScrolling, setIsAutoScrolling] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const autoScrollIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const router = useRouter();

    const BannerItem = ({ banner }: { banner: IBanner }) => {
        const handleClick = useCallback(() => {
            if (isValidString(banner?.deep_link)) {
                logBannerWidgetClick({
                    deep_link: banner?.deep_link || "",
                    image: banner?.image || "",
                });
                router.push(banner?.deep_link);
            }
        }, [banner]);

        return <BannerImage src={banner?.image} onClick={handleClick} />;
    };

    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
        const container = event.currentTarget;
        const scrollLeft = container.scrollLeft;
        const itemWidth = container.scrollWidth / banners?.length;
        const index = Math.round(scrollLeft / itemWidth);
        setActiveIndex(Math.min(index, banners?.length - 1));
        
        // Pause auto-scroll when user manually scrolls
        setIsAutoScrolling(false);
    };

    const scrollToNext = useCallback(() => {
        if (!containerRef.current || !isAutoScrolling) return;
        
        const nextIndex = (activeIndex + 1) % banners?.length;
        const itemWidth = containerRef.current.scrollWidth / banners?.length;
        const scrollLeft = nextIndex * itemWidth;
        
        containerRef.current.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
        });
        
        setActiveIndex(nextIndex);
    }, [activeIndex, banners?.length, isAutoScrolling]);

    // Auto-scroll effect
    useEffect(() => {
        if (!isAutoScrolling || banners?.length <= 1) return;

        autoScrollIntervalRef.current = setInterval(scrollToNext, 1000);

        return () => {
            if (autoScrollIntervalRef.current) {
                clearInterval(autoScrollIntervalRef.current);
            }
        };
    }, [scrollToNext, isAutoScrolling, banners?.length]);

    // Resume auto-scroll after 3 seconds of inactivity
    useEffect(() => {
        if (!isAutoScrolling) {
            const resumeTimeout = setTimeout(() => {
                setIsAutoScrolling(true);
            }, 3000);

            return () => clearTimeout(resumeTimeout);
        }
    }, [isAutoScrolling]);

    const totalBanners = banners?.length || 0;
    const shouldShowLeftIndicator = activeIndex > 0;
    const shouldShowRightIndicator = activeIndex < totalBanners - 1;

    if (!isValidData(banners)) {
        return null;
    }

    return (
        <>
            <BannersContainer ref={containerRef} onScroll={handleScroll}>
                {banners.map((banner, index) => (
                    <BannerItem key={index} banner={banner} />
                ))}
            </BannersContainer>
            <IndicatorsContainer>
                {shouldShowLeftIndicator && <IndicatorDot />}

                <ActiveIndicator variant={DLS_TEXT_VARIANTS.BODY_B3_REG}>
                    {activeIndex + 1}/{totalBanners}
                </ActiveIndicator>

                {shouldShowRightIndicator && <IndicatorDot />}
            </IndicatorsContainer>
        </>
    );
};

export default PromoWidgetSuccessView;
